// /src/components/layout/ChatColumn/SummaryMessage.jsx
import React, { forwardRef, useState, useCallback } from 'react';
import { Zap, Loader, AlertCircle, RefreshCw, Copy, Check, Trash2, Bookmark } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PlatformTag from '@/components/layout/sidebar/PlatformTag';

const SummaryMessage = forwardRef(({
  status,
  contentBlocks,
  error,
  platform,
  onRegenerate,
  onDelete,
  message,
}, ref) => {
  const [isCopied, setIsCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  /* ========================
   * extractContent - Get content from the right place in the message
   * ======================== */
  const extractContent = () => {
    if (message?.rawContent) return message.rawContent;
    if (message?.content) return message.content;
    if (contentBlocks?.length > 0) {
      const textBlock = contentBlocks.find(block => block.type === 'text');
      if (textBlock?.content) return textBlock.content;
    }
    if (typeof contentBlocks === 'string') {
      return contentBlocks;
    }
    console.error('🚨 Summary content not found. Available data:', {
      messageRawContent: message?.rawContent,
      messageContent: message?.content,
      contentBlocks,
      fullMessage: message
    });
    return '';
  };

  const content = extractContent();

  /* ========================
   * handleCopy - Copies the summary content to clipboard
   * ======================== */
  const handleCopy = useCallback(async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy summary:', error);
    }
  }, [content]);

  /* ========================
   * handleSaveAsSnippet - Saves summary as a snippet
   * ======================== */
  const handleSaveAsSnippet = useCallback(async () => {
    if (actionLoading || !content) return;
    setActionLoading('save');
    try {
      console.log('Save as snippet:', content);
    } catch (error) {
      console.error('Failed to save snippet:', error);
    } finally {
      setActionLoading(null);
    }
  }, [content, actionLoading]);

  /* ========================
   * handleDelete - Deletes the summary message
   * ======================== */
  const handleDelete = useCallback(async () => {
    if (actionLoading || !onDelete) return;
    setActionLoading('delete');
    try {
      await onDelete();
    } catch (error) {
      console.error('Failed to delete summary:', error);
    } finally {
      setActionLoading(null);
    }
  }, [onDelete, actionLoading]);

  const renderContent = () => {
    switch (status) {
      case 'generating':
        return (
          <div className="flex items-center gap-3 py-4">
            <Loader className="w-4 h-4 animate-spin" style={{ color: 'var(--primary)' }} />
            <span className="text-sm" style={{ color: 'var(--on-surface-dim)' }}>
              Generating conversation summary...
            </span>
          </div>
        );

      case 'error':
        return (
          <div className="py-3">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4" style={{ color: 'var(--error)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--error)' }}>
                Failed to generate summary
              </span>
            </div>
            <p className="text-sm mb-3" style={{ color: 'var(--on-surface-dim)' }}>
              {error || 'An unexpected error occurred while generating the summary.'}
            </p>
            {onRegenerate && (
              <button
                onClick={onRegenerate}
                className="flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors"
                style={{ 
                  background: 'var(--surface)',
                  color: 'var(--on-surface)',
                  border: '1px solid var(--border-color)'
                }}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Try Again
              </button>
            )}
          </div>
        );

      case 'completed':
      default:
        if (!content) {
          return (
            <div className="py-3">
              <p className="text-sm" style={{ color: 'var(--error)' }}>
                🚨 Summary content missing! Check console for debug info.
              </p>
            </div>
          );
        }

        return (
          <div className="py-3">
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-base font-semibold mb-2 mt-0" style={{ color: 'var(--on-surface)' }}>
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-sm font-semibold mb-2 mt-3" style={{ color: 'var(--on-surface)' }}>
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-sm font-medium mb-1 mt-2" style={{ color: 'var(--on-surface)' }}>
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-sm leading-relaxed mb-2" style={{ color: 'var(--on-surface-dim)' }}>
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul className="text-sm space-y-1 mb-2 ml-3 list-disc">
                      {children}
                    </ul>
                  ),
                  li: ({ children }) => (
                    <li style={{ color: 'var(--on-surface-dim)' }}>
                      {children}
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ color: 'var(--on-surface)', fontWeight: '600' }}>
                      {children}
                    </strong>
                  ),
                  code: ({ children }) => (
                    <code 
                      className="px-1 py-0.5 rounded text-xs font-mono"
                      style={{ 
                        background: 'var(--surface)',
                        color: 'var(--primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      {children}
                    </code>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote 
                      className="border-l-3 pl-3 italic mb-2"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      {children}
                    </blockquote>
                  )
                }}
              >
                {content}
              </ReactMarkdown>
            </div>

            <div 
              className="mt-3 pt-2 border-t flex items-center gap-2"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <div 
                className="w-1 h-1 rounded-full"
                style={{ background: 'var(--on-surface-dim)' }}
              />
              <p className="text-xs" style={{ color: 'var(--on-surface-dim)' }}>
                This summary is for reference and is not sent to the AI model
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div ref={ref} className="w-full my-3" data-message-type="summary">
      <div 
        className="group relative rounded-lg border transition-all duration-200"
        style={{ 
          background: 'var(--surface-dark)', 
          borderColor: 'var(--border-color)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div 
          className="flex items-center justify-between px-3 py-2 border-b"
          style={{ borderColor: 'var(--border-color)' }}
        >
          <div className="flex items-center space-x-2">
            <div 
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: 'var(--primary)' }}
            />
            <div className="flex items-center gap-2">
              <Zap className="w-3.5 h-3.5" style={{ color: 'var(--primary)' }} />
              <h3 
                className="text-xs font-medium"
                style={{ color: 'var(--on-surface)' }}
              >
                Conversation Summary
              </h3>
            </div>
            {platform && status !== 'generating' && (
              <PlatformTag platform={platform} hasKey={true} />
            )}
          </div>

          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {status === 'completed' && content && (
              <>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCopy}
                  className="p-1.5 rounded transition-colors duration-200"
                  style={{ 
                    background: isCopied ? 'var(--success-surface)' : 'var(--surface)',
                    color: isCopied ? 'var(--success)' : 'var(--on-surface)',
                    border: `1px solid ${isCopied ? 'var(--success)' : 'var(--border-color)'}`
                  }}
                  title="Copy summary"
                >
                  <AnimatePresence mode="wait">
                    {isCopied ? (
                      <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                        <Check className="w-3 h-3" />
                      </motion.div>
                    ) : (
                      <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                        <Copy className="w-3 h-3" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSaveAsSnippet}
                  disabled={actionLoading === 'save'}
                  className="p-1.5 rounded transition-colors duration-200"
                  style={{ 
                    background: 'var(--surface)',
                    color: 'var(--on-surface)',
                    border: '1px solid var(--border-color)',
                    opacity: actionLoading === 'save' ? 0.5 : 1
                  }}
                  title="Save as snippet"
                >
                  <Bookmark className="w-3 h-3" />
                </motion.button>
              </>
            )}

            {onDelete && (status === 'completed' || status === 'error') && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDelete}
                disabled={actionLoading === 'delete'}
                className="p-1.5 rounded transition-colors duration-200"
                style={{ 
                  background: 'var(--surface)',
                  color: 'var(--error)',
                  border: '1px solid var(--border-color)',
                  opacity: actionLoading === 'delete' ? 0.5 : 1
                }}
                title="Delete summary"
              >
                <Trash2 className="w-3 h-3" />
              </motion.button>
            )}
          </div>
        </div>

        <div className="px-3">
          {renderContent()}
        </div>

        <AnimatePresence>
          {actionLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center rounded-lg"
              style={{ background: 'rgba(0, 0, 0, 0.1)', backdropFilter: 'blur(2px)' }}
            >
              <div 
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ 
                  background: 'var(--surface)',
                  color: 'var(--on-surface)',
                  border: '1px solid var(--border-color)'
                }}
              >
                {actionLoading === 'save' ? 'Saving...' : 'Deleting...'}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

SummaryMessage.displayName = 'SummaryMessage';

export default SummaryMessage;
